import { getAuth, signOut } from "firebase/auth";
import { trackEvent, logoutAnalytics } from "../../libs/analyticsManager";

// A schedule save measured 2.6–2.8 s against staging (#4393 review).
const BEFORE_SIGN_OUT_TIMEOUT_MS = 8000;
const beforeSignOutTasks = new Set<() => unknown>();

/**
 * Work that must finish while the user can still write, e.g. an editor's
 * debounced autosave (#4132). Returns the unregister function.
 */
export const registerBeforeSignOut = (task: () => unknown) => {
  beforeSignOutTasks.add(task);
  return () => {
    beforeSignOutTasks.delete(task);
  };
};

// Capped so a failing or hung save can't block logout.
const runBeforeSignOut = async () => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.allSettled(
      [...beforeSignOutTasks].map((task) => Promise.resolve().then(task)),
    ),
    new Promise((resolve) => {
      timer = setTimeout(resolve, BEFORE_SIGN_OUT_TIMEOUT_MS);
    }),
  ]);
  clearTimeout(timer);
};

/**
 * Sign out, then a full page load of `path`: the Cloudflare Worker only serves
 * the marketing page on a real request to /, and a reload also wipes in-memory
 * store state (same pattern as ImpersonationContext). `beforeLoad` runs after
 * the sign-out settles, before the events and the load.
 */
export const signOutAndGo = async (path: string, beforeLoad?: () => void) => {
  await runBeforeSignOut();
  sessionStorage.clear();
  return signOut(getAuth()).finally(() => {
    beforeLoad?.();
    trackEvent("Logout");
    logoutAnalytics();
    window.location.assign(path);
  });
};
